import styled from 'styled-components';
import LS from '../styles/layoutStyled';
import CS from '../styles/componentStyled';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import React, { useState, useMemo, useEffect } from 'react';
import { useSetRecoilState, useRecoilValue, useRecoilState } from 'recoil';
import {
  blankDefaultMsgRecoil,
  errorMsgRecoil,
  confirmMsgRecoil,
} from '../atom/ModalAtom';
import ConfirmModal from '../modal/ConfirmModal';
import { RoomPeoplesDto, RoomPeopleDto, RoomInforDto } from '../model/room.dto';
import WorkNum from './WorkNum';
import { userIdState } from '../atom/TokenAtom';
import { socketio } from '../api/socket.api';
import { roomAPI } from '../api/axiosIndex';
import { Icon } from './Icon';

interface RoomDetailSideBoxProps {
  xBtnClick: () => void;
  roomPeoples: RoomPeoplesDto | null;
  userPosition: string | undefined;
  roomId: number | undefined;
  borrowedIds: number[] | null;
  roomInfor: RoomInforDto | null;
  onListUpdate: () => void;
}

const RoomDetailSideBox = ({
  xBtnClick,
  roomPeoples,
  userPosition,
  roomId,
  borrowedIds,
  roomInfor,
  onListUpdate,
}: RoomDetailSideBoxProps) => {
  const blankMsg = useRecoilValue(blankDefaultMsgRecoil);
  const setError = useSetRecoilState(errorMsgRecoil);
  const setConfirm = useSetRecoilState(confirmMsgRecoil);

  const [userId, setUserId] = useRecoilState(userIdState);

  const navigate = useNavigate();

  const [selectUser, setSelectUser] = useState<RoomPeopleDto>();
  const [selectUserName, setSelectUserName] = useState<string>('');

  const [memberDelState, setMemberDelState] = useState(false);
  const [memberAllDelState, setMemberAllDelState] = useState(false);
  const [roomDelState, setRoomDelState] = useState(false);
  const [roomOutState, setRoomOutState] = useState(false);

  const myRoomId = roomPeoples?.find((people) => people.userId === userId)?.id;
  const roomLineIds = roomInfor?.lines.map((line) => line.id)
    ? roomInfor?.lines.map((line) => line.id)
    : null;

  // 내보내기
  const handleMemberDel = async () => {
    try {
      await roomAPI.delRoomOut([String(selectUser?.id)]);
      for (let i = 0; i < Number(roomLineIds?.length); i++) {
        socketio.entry(Number(roomLineIds?.[i]));
      }
      socketio.participation(Number(roomId));
      setMemberDelState(false);
      onListUpdate();
      setConfirm(`${selectUserName} 님을 내보냈습니다.`);
    } catch {
      setError('내보내기에 실패하였습니다.');
    }
  };

  // 용차 전부 내보내기
  const handleMemberAllDel = async () => {
    if (borrowedIds?.length === 0) {
      setError('내보낼 용차가 없습니다.');
      setMemberAllDelState(false);
      return;
    }

    try {
      await roomAPI.delRoomOutBorroweds(borrowedIds?.map(String) ?? []);
      for (let i = 0; i < Number(roomLineIds?.length); i++) {
        socketio.entry(Number(roomLineIds?.[i]));
      }
      socketio.participation(Number(roomId));
      setMemberAllDelState(false);
      onListUpdate();
      setConfirm('용차를 전부 내보냈습니다.');
    } catch (error) {
      setError('용차 내보내기에 실패했습니다.');
      setMemberAllDelState(false);
    }
  };

  // 방 삭제
  const handleRoomDel = async () => {
    try {
      await roomAPI.delRoom([String(roomId)]);
      for (let i = 0; i < Number(roomLineIds?.length); i++) {
        socketio.entry(Number(roomLineIds?.[i]));
      }
      socketio.participation(Number(roomId));
      setRoomDelState(false);
      navigate('/myroomlist');
      setConfirm('방이 삭제되었습니다.');
    } catch {
      setError('방 삭제에 실패하였습니다.');
    }
  };

  // 방 나가기
  const handleRoomOut = async () => {
    try {
      await roomAPI.delRoomOut([String(myRoomId)]);
      for (let i = 0; i < Number(roomLineIds?.length); i++) {
        socketio.entry(Number(roomLineIds?.[i]));
      }
      socketio.participation(Number(roomId));
      setRoomOutState(false);
      navigate('/myroomlist');
      setConfirm('방을 나갔습니다.');
    } catch {
      setError('방 나가기에 실패하였습니다.');
    }
  };

  // 방 내보내기 x버튼 클릭 이벤트
  const handleUserRoomOut = (person: RoomPeopleDto) => {
    setSelectUser(person);
    setSelectUserName(person.userNumber);
    setMemberDelState(true);
  };

  return (
    <>
      <Wrap>
        <TopBox>
          <button className="img-box" type="button" onClick={xBtnClick}>
            <Icon name="CloseIcon" size="100%" color="#949494" />
          </button>
        </TopBox>
        <ListBox>
          <li>
            <h2>방장</h2>
            <ul>
              {roomPeoples
                ?.filter((person) => person.position === 'Master')
                .map((person) => (
                  <li key={person.id}>
                    <div className="left-box">
                      <span className="img-box">
                        <Icon name="UserIcon" size="100%" color="#4E5565" />
                        <span className="img-box">
                          <Icon
                            name="KingBgIcon"
                            size="100%"
                            color="#ffffff"
                            color2="#FABC43"
                          />
                        </span>
                      </span>
                      {person.userId === userId && (
                        <span className="my-text">나</span>
                      )}
                      <UserInfoWrap>
                        <h3 className="name">{person.userNumber}</h3>
                        <p>{person.otherText}</p>
                      </UserInfoWrap>
                    </div>
                    <div className="right-box">
                      {userPosition !== 'Borrowed' && (
                        <ul className="work-num-box">
                          <li>
                            <h4>오늘</h4>
                            <span className="num">
                              <WorkNum listId={person.userId} type="today" />건
                            </span>
                          </li>
                          <li>
                            <h4>누적</h4>
                            <span className="num">
                              <WorkNum listId={person.userId} type="month" />건
                            </span>
                          </li>
                        </ul>
                      )}
                    </div>
                  </li>
                ))}
            </ul>
          </li>
          <li>
            <h2>고정</h2>
            <ul>
              {roomPeoples
                ?.filter((person) => person.position === 'Belonged')
                .map((person) => (
                  <li key={person.id} className="fix">
                    <div className="left-box">
                      <span className="img-box">
                        <Icon name="UserIcon" size="100%" color="#4E5565" />
                      </span>
                      {person.userId === userId && (
                        <span className="my-text">나</span>
                      )}
                      <UserInfoWrap>
                        <h3 className="name">{person.userNumber}</h3>
                        <p>{person.otherText}</p>
                      </UserInfoWrap>
                    </div>
                    <div className="right-box">
                      {userPosition !== 'Borrowed' && (
                        <ul className="work-num-box">
                          <li>
                            <h4>오늘</h4>
                            <span className="num">
                              <WorkNum listId={person.userId} type="today" />건
                            </span>
                          </li>
                          <li>
                            <h4>누적</h4>
                            <span className="num">
                              <WorkNum listId={person.userId} type="month" />건
                            </span>
                          </li>
                        </ul>
                      )}
                      {userPosition === 'Master' && (
                        <button
                          className="x-btn img-box"
                          onClick={() => handleUserRoomOut(person)}
                        >
                          <Icon
                            name="CloseBgIcon"
                            size="100%"
                            color="#FFFFFF"
                            color2="#D65757"
                          />
                        </button>
                      )}
                    </div>
                  </li>
                ))}
            </ul>
          </li>
          <li>
            <h2>용차</h2>
            <ul>
              {roomPeoples
                ?.filter((person) => person.position === 'Borrowed')
                .map((person) => (
                  <li key={person.id} className="basic">
                    <div className="left-box">
                      <span className="img-box">
                        <Icon name="UserIcon" size="100%" color="#4E5565" />
                      </span>
                      {person.userId === userId && (
                        <span className="my-text">나</span>
                      )}
                      <UserInfoWrap>
                        <h3 className="name">{person.userNumber}</h3>
                        <p>{person.otherText}</p>
                      </UserInfoWrap>
                    </div>
                    <div className="right-box">
                      {userPosition === 'Master' && (
                        <button
                          className="x-btn img-box"
                          onClick={() => handleUserRoomOut(person)}
                        >
                          <Icon
                            name="CloseBgIcon"
                            size="100%"
                            color="#FFFFFF"
                            color2="#D65757"
                          />
                        </button>
                      )}
                    </div>
                  </li>
                ))}
            </ul>
          </li>
        </ListBox>
        {userPosition === 'Master' && (
          <BtnBox>
            <CS.Btn4Type1
              type="button"
              onClick={() => setMemberAllDelState(true)}
            >
              용차 내보내기
            </CS.Btn4Type1>
            <CS.Btn4Type2 type="button" onClick={() => setRoomDelState(true)}>
              방 삭제
            </CS.Btn4Type2>
          </BtnBox>
        )}
        {userPosition !== 'Master' && (
          <BtnBox>
            <CS.Btn4Type1 type="button" onClick={() => setRoomOutState(true)}>
              방 나가기
            </CS.Btn4Type1>
          </BtnBox>
        )}
      </Wrap>

      {memberDelState && (
        <ConfirmModal
          text={`정말 ${selectUserName}님을 내보내시겠습니까?`}
          onCancel={() => setMemberDelState(false)}
          onConfirm={handleMemberDel}
        />
      )}

      {memberAllDelState && (
        <ConfirmModal
          text="용차를 전부 내보내시겠습니까?"
          onCancel={() => setMemberAllDelState(false)}
          onConfirm={handleMemberAllDel}
        />
      )}

      {roomDelState && (
        <ConfirmModal
          text="정말 방을 삭제하시겠습니까?"
          onCancel={() => setRoomDelState(false)}
          onConfirm={handleRoomDel}
        />
      )}

      {roomOutState && (
        <ConfirmModal
          text="정말 방을 나가시겠습니까?"
          onCancel={() => setRoomOutState(false)}
          onConfirm={handleRoomOut}
        />
      )}
    </>
  );
};

export default RoomDetailSideBox;

const Wrap = styled.div`
  width: 100%;
  height: 100vh;
  background-color: #fff;
  position: fixed;
  z-index: 5;
  top: 0;
  left: 0;
`;
const TopBox = styled.div`
  width: 100%;
  height: 108px;
  position: relative;

  > button {
    width: 24px;
    position: absolute;
    bottom: 28px;
    right: 16px;
  }
`;
const ListBox = styled.ul`
  height: calc(100vh - 256px);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 45px;
  box-sizing: border-box;
  padding: 0 16px 16px 16px;

  > li {
    display: flex;
    flex-direction: column;
    gap: 12px;

    > h2 {
      font-size: 15px;
      font-weight: 700;
    }

    > ul {
      display: flex;
      flex-direction: column;
      gap: 16px;

      > li {
        display: flex;
        align-items: center;
        justify-content: space-between;
        &.fix {
          .name {
            color: ${(props) => props.theme.mainColor1} !important;
          }
        }
        &.basic {
          .name {
            color: ${(props) => props.theme.subColor3} !important;
          }
        }

        .left-box {
          display: flex;
          align-items: center;
          gap: 8px;

          > .img-box {
            width: 24px;
            position: relative;
            > .img-box {
              position: absolute;
              bottom: 0;
              right: 0;
              width: 12px;
            }
          }

          .my-text {
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background-color: ${(props) => props.theme.mainColor2};
            font-size: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #fff;
          }

          .name {
            font-size: 14px;
            color: #333;
          }
        }

        .right-box {
          display: flex;
          align-items: center;
          gap: 20px;

          > ul {
            display: flex;
            gap: 12px;

            > li {
              display: flex;
              flex-direction: column;
              text-align: center;
              gap: 6px;

              > h4 {
                font-size: 12px;
                color: #666;
              }

              > .num {
                font-size: 12px;
                color: #4e5565;
              }
            }
          }

          .x-btn {
            width: 16px;
          }
        }
      }
    }
  }
`;

const BtnBox = styled.div`
  width: 100%;
  height: 148px;
  box-sizing: border-box;
  padding: 20px 16px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 12px;
`;

export const UserInfoWrap = styled.div`
  display: flex;
  flex-direction: column;

  > p {
    font-size: 14px;
    color: #4e5565;
    line-height: 1.4;
  }
`;
